export interface AssurantQuoteRequest {
  readonly marca: string;
  readonly modelo: string;
  readonly vin: string;
  readonly canal_venta: string;
  readonly categoria_uso: string;
  readonly plazo_seguro: number;
  readonly valor_factura: number;
  readonly certificado?: string;
  readonly rfc?: string;
}

/**
 * Raw financial fields extracted from the nested JSON-string `result` field
 * returned by the external Assurant quote API (broker). Keys are dictated by
 * Assurant and kept in Spanish; `AssurantService.quoteGap` maps this to the
 * English `AssurantGapData` used everywhere else in the app.
 */
export interface AssurantQuoteRawData {
  readonly sisRamoId: number;
  readonly productoId: number;
  readonly fDesde: string;
  readonly fHasta: string;
  readonly cantidadCuotas: number;
  readonly montoCuota: number;
  readonly montoPoliza: number;
  readonly montoSuma: number;
}

/**
 * GAP quote data in the app's internal English shape (mapped from the broker's
 * Spanish response). This is what gets stored on the quote and exchanged with
 * the prestemos backend.
 */
export interface AssurantGapData {
  readonly branchId: number;
  readonly productId: number;
  readonly dateFrom: string;
  readonly dateTo: string;
  readonly installmentsCount: number;
  readonly installmentAmount: number;
  readonly policyAmount: number;
  readonly insuredAmount: number;
  /** Financed monthly installment (with interest), added by the prestemos backend. */
  readonly installmentAmountWithInterest?: number;
}

/**
 * Legacy Spanish GAP keys persisted on quotes/applications before the English
 * rename. Kept so loaders can normalize historical payloads.
 */
export interface AssurantGapLegacyPayload {
  readonly sisRamoId?: number;
  readonly productoId?: number;
  readonly fDesde?: string;
  readonly fHasta?: string;
  readonly cantidadCuotas?: number;
  readonly montoCuota?: number;
  readonly montoPoliza?: number;
  readonly montoSuma?: number;
  /** Financed monthly installment (with interest) under the pre-rename key. */
  readonly montoCuotaConInteres?: number;
}

/** Any GAP shape the app may encounter: broker raw, English, or legacy persisted. */
export type AssurantGapLike =
  | AssurantGapData
  | AssurantQuoteRawData
  | AssurantGapLegacyPayload
  | (Partial<AssurantGapData> & AssurantGapLegacyPayload);

/**
 * Normalizes broker Spanish / legacy persisted GAP payloads into AssurantGapData.
 * Prefers English keys when both shapes are present so new writes stay canonical.
 */
export function normalizeAssurantGapData(raw: AssurantGapLike | null | undefined): AssurantGapData | null {
  if (!raw) {
    return null;
  }

  const r = raw as Partial<AssurantGapData> & AssurantGapLegacyPayload;

  const branchId = r.branchId ?? r.sisRamoId;
  const productId = r.productId ?? r.productoId;
  const dateFrom = r.dateFrom ?? r.fDesde;
  const dateTo = r.dateTo ?? r.fHasta;
  const installmentsCount = r.installmentsCount ?? r.cantidadCuotas;
  const installmentAmount = r.installmentAmount ?? r.montoCuota;
  const policyAmount = r.policyAmount ?? r.montoPoliza;
  const insuredAmount = r.insuredAmount ?? r.montoSuma;
  const installmentAmountWithInterest = r.installmentAmountWithInterest ?? r.montoCuotaConInteres;

  if (branchId == null && productId == null && policyAmount == null && installmentAmount == null) {
    return null;
  }

  return {
    branchId: Number(branchId ?? 0),
    productId: Number(productId ?? 0),
    dateFrom: dateFrom ?? "",
    dateTo: dateTo ?? "",
    installmentsCount: Number(installmentsCount ?? 0),
    installmentAmount: Number(installmentAmount ?? 0),
    policyAmount: Number(policyAmount ?? 0),
    insuredAmount: Number(insuredAmount ?? 0),
    ...(installmentAmountWithInterest != null
      ? { installmentAmountWithInterest: Number(installmentAmountWithInterest) }
      : {}),
  };
}

export interface AssurantQuoteResponse {
  readonly status: string;
  readonly data: AssurantQuoteRawData | null;
  readonly errors?: unknown;
}

/**
 * SRA price option served by the backend catalog (`/api/assurant_sra_prices`).
 * `total_price` is the full policy price; the quoter prorates it across the
 * selected term to obtain the monthly SRA amount (same approach as GAP).
 */
export interface AssurantSraPriceOption {
  readonly id?: number;
  readonly value: string;
  readonly label: string;
  readonly total_price: number;
  readonly external_id?: string | null;
}
